import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import Calendario from '../components/Calendario'

const API = process.env.REACT_APP_API_URL || ''

const getDiaLabel = (fechaStr) => {
  if (!fechaStr) return null
  const [y, m, d] = fechaStr.split('-').map(Number)
  const dia = new Date(y, m - 1, d).getDay()
  if (dia === 0) return null
  if (dia === 6) return 'Sábados 07:30 – 12:30'
  return 'Lunes a Viernes 07:30 – 16:00'
}

export default function Reservas() {
  const [form, setForm] = useState({
    nombre: '', email: '', telefono: '',
    fecha: '', hora: '', personas: '2', notas: '',
    ubicacion: 'interior', tipo: 'mesa'
  })
  const [slots, setSlots] = useState([])
  const [cargandoSlots, setCargandoSlots] = useState(false)
  const [error, setError] = useState('')
  const [exito, setExito] = useState('')
  const [cargando, setCargando] = useState(false)

  const diaLabel = getDiaLabel(form.fecha)
  const esDomingo = form.fecha && !diaLabel

  const personasOpciones = form.tipo === 'salon_privado'
    ? Array.from({ length: 21 }, (_, i) => i + 20)
    : Array.from({ length: 19 }, (_, i) => i + 1)

  const cargarSlots = useCallback(async () => {
    if (!form.fecha || esDomingo) { setSlots([]); return }
    setCargandoSlots(true)
    try {
      const res = await axios.get(`${API}/api/reservas/disponibilidad`, {
        params: { fecha: form.fecha, zona: form.ubicacion, tipo: form.tipo, personas: form.personas }
      })
      setSlots(res.data.slots || [])
      if (form.hora) {
        const slotActual = res.data.slots?.find(s => s.hora === form.hora)
        if (!slotActual?.disponible) setForm(f => ({ ...f, hora: '' }))
      }
    } catch {
      setSlots([])
    } finally {
      setCargandoSlots(false)
    }
  }, [form.fecha, form.ubicacion, form.tipo, form.personas])

  useEffect(() => { cargarSlots() }, [cargarSlots])

  const setTipo = tipo => setForm(f => ({
    ...f, tipo, hora: '', personas: tipo === 'salon_privado' ? '20' : '2'
  }))

  const handleSubmit = async e => {
    e.preventDefault()
    setError(''); setExito('')

    if (esDomingo) { setError('Los domingos el local está cerrado.'); return }
    if (!form.hora) { setError('Selecciona una hora.'); return }

    setCargando(true)
    try {
      await axios.post(`${API}/api/reservas/`, form)
      setExito('¡Reserva enviada! Recibirás un email de confirmación en breve.')
      setForm({ nombre: '', email: '', telefono: '', fecha: '', hora: '', personas: '2', notas: '', ubicacion: 'interior', tipo: 'mesa' })
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear la reserva')
    } finally {
      setCargando(false)
    }
  }

  return (
    <div className="form-page">
      <div className="form-card" style={{ maxWidth: 600 }}>
        <p className="page-label">El Rincón de la Escandella</p>
        <h1>Reservar mesa</h1>
        <p className="subtitle">Completa el formulario y te confirmaremos la reserva por email</p>

        {error && <div className="error-msg">{error}</div>}
        {exito && <div className="success-msg">{exito}</div>}

        <form onSubmit={handleSubmit}>

          {/* Datos de contacto */}
          <div className="form-group">
            <label>Nombre completo <span style={{ color: 'var(--gold)' }}>*</span></label>
            <input
              type="text"
              value={form.nombre}
              onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              placeholder="Tu nombre y apellidos"
              required
            />
          </div>

          <div className="form-group">
            <label>Email <span style={{ color: 'var(--gold)' }}>*</span></label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="Para enviarte la confirmación"
              required
            />
          </div>

          <div className="form-group">
            <label>Teléfono <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>(opcional)</span></label>
            <input
              type="tel"
              value={form.telefono}
              onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))}
              placeholder="Por si necesitamos contactarte"
            />
          </div>

          {/* Tipo */}
          <div className="form-group">
            <label>Tipo de reserva</label>
            <div className="toggle-group">
              <button type="button" className={`toggle-btn ${form.tipo === 'mesa' ? 'activo' : ''}`} onClick={() => setTipo('mesa')}>
                Mesa
              </button>
              <button type="button" className={`toggle-btn ${form.tipo === 'salon_privado' ? 'activo' : ''}`} onClick={() => setTipo('salon_privado')}>
                Salón Privado · Eventos
              </button>
            </div>
            {form.tipo === 'salon_privado' && (
              <p className="form-hint">Salón privado para grupos de 20 a 40 personas.</p>
            )}
          </div>

          {/* Zona */}
          <div className="form-group">
            <label>Zona</label>
            <div className="toggle-group">
              <button type="button" className={`toggle-btn ${form.ubicacion === 'interior' ? 'activo' : ''}`} onClick={() => setForm(f => ({ ...f, ubicacion: 'interior', hora: '' }))}>
                Interior
              </button>
              <button type="button" className={`toggle-btn ${form.ubicacion === 'terraza' ? 'activo' : ''}`} onClick={() => setForm(f => ({ ...f, ubicacion: 'terraza', hora: '' }))}>
                Terraza
              </button>
            </div>
          </div>

          {/* Personas */}
          <div className="form-group">
            <label>Número de personas</label>
            <select name="personas" value={form.personas} onChange={e => setForm(f => ({ ...f, personas: e.target.value, hora: '' }))}>
              {personasOpciones.map(n => (
                <option key={n} value={n}>{n} {n === 1 ? 'persona' : 'personas'}</option>
              ))}
            </select>
          </div>

          {/* Fecha */}
          <div className="form-group">
            <label>Fecha</label>
            <Calendario
              value={form.fecha}
              onChange={fecha => setForm(f => ({ ...f, fecha, hora: '' }))}
              diasCerrados={[0]}
              placeholder="Elige el día de la reserva"
            />
            {esDomingo && <span className="form-warn">Domingo — local cerrado</span>}
            {diaLabel && <span className="form-hint-inline">{diaLabel}</span>}
          </div>

          {/* Slots de hora */}
          {form.fecha && !esDomingo && (
            <div className="form-group">
              <label>Hora disponible</label>
              {cargandoSlots ? (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Comprobando disponibilidad...</p>
              ) : slots.length === 0 ? (
                <p style={{ fontSize: '0.75rem', color: '#e74c3c', marginTop: '0.5rem' }}>No hay disponibilidad para esta fecha.</p>
              ) : (
                <div className="slots-grid">
                  {slots.map(s => (
                    <button
                      key={s.hora}
                      type="button"
                      disabled={!s.disponible}
                      className={`slot-btn ${form.hora === s.hora ? 'activo' : ''} ${!s.disponible ? 'ocupado' : ''}`}
                      onClick={() => s.disponible && setForm(f => ({ ...f, hora: s.hora }))}
                    >
                      {s.hora}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notas */}
          <div className="form-group">
            <label>Notas <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>(opcional)</span></label>
            <textarea
              name="notas" value={form.notas}
              onChange={e => setForm(f => ({ ...f, notas: e.target.value }))}
              placeholder="Alergias, ocasión especial, solicitudes..."
            />
          </div>

          <button type="submit" className="btn-primary" disabled={cargando || esDomingo || !form.hora}>
            {cargando ? 'Enviando...' : 'Solicitar Reserva'}
          </button>
        </form>
      </div>
    </div>
  )
}
